<script setup lang="ts">
import PositionablePopover from '@/core/dialogs/PositionablePopover.vue';
import ClickableListItem from '@/core/elements/ClickableListItem.vue';
import CustomButton from '@/core/elements/CustomButton.vue';
import CustomCheckbox from '@/core/elements/CustomCheckbox.vue';
import { IDropdownMultiSelectAction, ISelectOption } from '@/interfaces';
import { EPosition } from 'common/enum/misc.enum';
import { ref, toRefs, useTemplateRef } from 'vue';
import ScrollableListWithIndicators from './ScrollableListWithIndicators.vue';

const emit = defineEmits<{
  'click:action': [];
}>();

const props = defineProps<{
  action: IDropdownMultiSelectAction;
  anchorButtonRef?: HTMLElement;
}>();
const { action, anchorButtonRef } = toRefs(props);

const dialogRef = useTemplateRef('dialogRef');
const selectedOptions = ref<ISelectOption<string>[]>([]);

const isSelected = (option: ISelectOption<string>): boolean => {
  return selectedOptions.value.some((o) => o.value === option.value);
};

const handleOptionClick = (option: ISelectOption<string>): void => {
  if (isSelected(option)) {
    selectedOptions.value = selectedOptions.value.filter((o) => o.value !== option.value);
  } else {
    selectedOptions.value = [...selectedOptions.value, option];
  }
};

const handleActionClick = async (): Promise<void> => {
  await action.value.action(selectedOptions.value.map((o) => o.value));
  emit('click:action');
  hide();
};

const hide = (): void => {
  dialogRef.value?.hide();
  selectedOptions.value = [];
};

const show = (): void => {
  dialogRef.value?.show();
};

defineExpose({
  show,
});
</script>

<template>
  <PositionablePopover
    ref="dialogRef"
    scroll-mode="content"
    :anchor-element="anchorButtonRef"
    :position="EPosition.BOTTOM_LEFT"
    flip-strategy="both"
    :min-content-height="200"
  >
    <div :class="['flex min-h-0 flex-col', 'bg-surface-secondary rounded-md shadow-popover', 'min-w-70 py-sm']">
      <ScrollableListWithIndicators max-height="18rem">
        <div class="py-sm">
          <div v-for="(group, groupIndex) of action.groups" :key="`${group.label}-${groupIndex}`">
            <div v-if="group.label" class="text-footer text-muted px-md py-sm">{{ group.label }}</div>
            <div v-for="(option, optionIndex) of group.options" :key="`${group.label}-${optionIndex}`">
              <ClickableListItem tabindex="0" class="h-cell !p-md outline-inner" @click="handleOptionClick(option)">
                <CustomCheckbox
                  class="w-full pointer-events-none"
                  type="rtl"
                  :model-value="isSelected(option)"
                  :label="option.label"
                >
                  <span>{{ option.label }}</span>
                </CustomCheckbox>
              </ClickableListItem>
            </div>
          </div>
        </div>
      </ScrollableListWithIndicators>
      <div class="shrink-0 px-md py-sm bg-surface-secondary">
        <CustomButton class="w-full justify-center" :disabled="!selectedOptions.length" @click.stop="handleActionClick">
          {{ action.actionLabel }}
        </CustomButton>
      </div>
    </div>
  </PositionablePopover>
</template>
